"use client";

import { useId } from "react";
import { motion, useReducedMotion } from "framer-motion";

type TimelineDotProps = {
    isActive: boolean;
    inactiveSize?: number;
    showHoverPulse?: boolean;
    className?: string;
};

const TRANSITION = { duration: 0.3, ease: [0.4, 0, 0.2, 1] };
const TRANSITION_NONE = { duration: 0 };

const TimelineDot = ({
    isActive,
    inactiveSize = 11,
    showHoverPulse = false,
    className,
}: TimelineDotProps) => {
    const uid = useId();
    const gradientId = `${uid}-radial`;
    const prefersReducedMotion = useReducedMotion();
    const t = prefersReducedMotion ? TRANSITION_NONE : TRANSITION;
    const r = inactiveSize / 2;

    return (
        <div className={`relative flex items-center justify-center w-8 h-8 ${className ?? ""}`}>
            <motion.svg
                width="33" height="33" viewBox="0 0 33 33" fill="none"
                className="absolute inset-0"
                animate={{ opacity: isActive ? 1 : 0, scale: isActive ? 1 : 0.3 }}
                transition={t}
            >
                <circle cx="16.5" cy="16.5" r="9.16667" transform="rotate(-180 16.5 16.5)" fill="#9AB1F5" stroke="#3654AE" strokeWidth="1.33333" />
                <circle cx="16.5" cy="16.5" r="16.5" fill={`url(#${gradientId})`} />
                <defs>
                    <radialGradient id={gradientId} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(16.5 16.5) rotate(90) scale(16.5)">
                        <stop stopColor="#5472CC" />
                        <stop offset="0.697115" stopColor="#5472CC" stopOpacity="0.25" />
                    </radialGradient>
                </defs>
            </motion.svg>

            <motion.svg
                width={inactiveSize} height={inactiveSize}
                viewBox={`0 0 ${inactiveSize} ${inactiveSize}`}
                fill="none"
                className="absolute z-[1]"
                animate={{ opacity: isActive ? 0 : 1, scale: isActive ? 0.5 : 1 }}
                transition={t}
            >
                <circle cx={r} cy={r} r={r} fill="#183690" />
            </motion.svg>

            {showHoverPulse && !isActive && (
                <div className="absolute inset-0 m-auto w-[17px] h-[17px] scale-0 group-hover/step:scale-100 transition-transform duration-500 ease-out">
                    <div className="w-full h-full rounded-full bg-primary300 group-hover/step:animate-pulse-dot" />
                </div>
            )}
        </div>
    );
};

export default TimelineDot;
